/**
 * Audio Service for gallery background music
 */

export interface MusicTrack {
  id: string;
  name: string;
  artist?: string;
  duration?: string;
  genre?: string;
  mood?: string;
}

export interface AudioLoadState {
  isLoading: boolean;
  error: string | null;
  usingSilentFallback?: boolean;
}

// Simple silent MP3 base64 data
const SILENT_MP3_BASE64 = 'SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjQ1LjEwMAAAAAAAAAAAAAAA//tQwAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAAoAAAyAABA7QAA//8AAAA5wMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAAAAAAAAAAAAA//tQxAACHGUlQhGYwlIDAEZk1TdFHRMQAAK27ReFk0MH4WUAoLB8HwfB8+p6rAGbEy19zGOEq1O/PDIEAE/8EAHf8EAE/+gQBPPgQBN4IAAT/4EAT//EoDN5QQAAgCnD+KK//tQxAgCGgIGTBmZAANMwMnLMzYAIABkwKAQZgKAQZkCRYEhQOCgcFRMXFxcXDg4ODgoJCQkJRFxa3EAmBcEyAgQDBUAL+XMKgMHS4OzITBQeTJgMJCgDgpAoVBYKRMCwUiYLBWJgaBT//tQxAaAAcYEUNNPEAAQwIn2bQMAYXA0KRMLgaFA4KRQLC4LhaKTcKRWLhaKRWLxaLRcLhaLhcLhcLhcLhcLxeLheLhcLheLheLheKRSKRSKRSLRaLRaLRaLRaLRaLRaLRcLRaLRUKRSKRQ==';

// Define music tracks 
export const musicTracks: MusicTrack[] = [
  { 
    id: 'track1', 
    name: 'Cheerful Memories',
    artist: 'AShamaluevMusic',
    duration: '3:05',
    genre: 'Ambient',
    mood: 'Happy',
  },
  { 
    id: 'track2', 
    name: 'Adventure Time',
    artist: 'Infraction',
    duration: '2:45',
    genre: 'Electronic',
    mood: 'Upbeat',
  },
  { 
    id: 'track3', 
    name: 'Relaxing Waves',
    artist: 'kvgarlic',
    duration: '3:22',
    genre: 'Nature',
    mood: 'Peaceful',
  },
  { 
    id: 'track4', 
    name: 'Family Moments',
    artist: 'Purrple Cat',
    duration: '2:56',
    genre: 'Acoustic',
    mood: 'Nostalgic',
  },
  { 
    id: 'track5', 
    name: 'Travel Vibes',
    artist: 'Music_Unlimited',
    duration: '2:30',
    genre: 'World Music',
    mood: 'Inspiring',
  }
];

// Map of track IDs to their file names
const trackFileMap: {[key: string]: string} = {
  'track1': 'cheerful-memories.mp3',
  'track2': 'adventure-time.mp3',
  'track3': 'relaxing-waves.mp3',
  'track4': 'family-moments.mp3',
  'track5': 'travel-vibes.mp3'
};

class AudioService {
  private audioCache: Map<string, HTMLAudioElement> = new Map();
  private loadingStates: Map<string, AudioLoadState> = new Map();
  private useSilentAudioOnly: boolean = false;
  
  /**
   * Get a music track by ID
   */
  getTrackById(trackId: string): MusicTrack | undefined {
    return musicTracks.find(track => track.id === trackId);
  }
  
  /**
   * Get all available music tracks
   */
  getAllTracks(): MusicTrack[] {
    return [...musicTracks];
  }
  
  /**
   * Create a silent audio element
   * @returns Audio element
   */
  private createSilentAudio(): HTMLAudioElement {
    const audio = new Audio(`data:audio/mp3;base64,${SILENT_MP3_BASE64}`);
    audio.volume = 0;
    return audio;
  }

  /**
   * Force silent mode only (useful when real audio consistently fails)
   */
  forceSilentMode(force: boolean = true): void {
    this.useSilentAudioOnly = force;

    
    // Clear cache if changing modes
    if (force) {
      this.clearCache();
    }
  }
  
  /**
   * Get an audio element for a track, trying real audio first, then fallback to silent
   */
  getAudio(trackId: string): Promise<HTMLAudioElement> {
    // Check cache first
    const cachedAudio = this.audioCache.get(trackId);
    if (cachedAudio) {
      return Promise.resolve(cachedAudio);
    }
    
    // Get track data
    const track = this.getTrackById(trackId);
    if (!track) {
      return Promise.reject(new Error(`Track not found: ${trackId}`));
    }
    
    // Update loading state
    this.loadingStates.set(trackId, {
      isLoading: true,
      error: null
    });
    
    // If we're in silent-only mode, skip trying real audio
    if (this.useSilentAudioOnly) {
      return this.loadSilentAudio(trackId, track);
    }
    
    // Try loading real audio first
    return this.loadRealAudio(trackId, track)
      .catch(error => {
        console.warn(`Failed to load real audio for ${track.name}, using silent fallback`, error);
        return this.loadSilentAudio(trackId, track, true);
      });
  }
  
  /**
   * Load a real audio file
   */
  private loadRealAudio(trackId: string, track: MusicTrack): Promise<HTMLAudioElement> {
    return new Promise((resolve, reject) => {
      try {
        // Get filename for this track
        const filename = trackFileMap[trackId];
        if (!filename) {
          return reject(new Error(`No audio file mapped for track: ${trackId}`));
        }
        
        // Create absolute path to public audio file
        const audioUrl = `/audio/${filename}`;

        
        const audio = new Audio(audioUrl);
        
        // Set load timeout (5 seconds)
        const timeoutId = setTimeout(() => {
          reject(new Error(`Timeout loading audio: ${audioUrl}`));
        }, 5000);
        
        // Handle successful load
        audio.addEventListener('canplaythrough', () => {
          clearTimeout(timeoutId);
          
          // Cache the audio
          this.audioCache.set(trackId, audio);
          
          // Update loading state
          this.loadingStates.set(trackId, {
            isLoading: false,
            error: null,
            usingSilentFallback: false
          });
          

          resolve(audio);
        }, { once: true });
        
        // Handle load error
        audio.addEventListener('error', (e) => {
          clearTimeout(timeoutId);
          console.error(`Audio load error for ${audioUrl}:`, e);
          reject(new Error(`Failed to load audio file: ${audioUrl}`));
        }, { once: true });
        
        // Start loading
        audio.load();
      } catch (error) {
        reject(error);
      }
    });
  }
  
  /**
   * Load a silent audio element as fallback
   */
  private loadSilentAudio(trackId: string, track: MusicTrack, isFallback: boolean = false): Promise<HTMLAudioElement> {
    // Create silent audio
    const audio = this.createSilentAudio();
    
    // Cache the audio
    this.audioCache.set(trackId, audio);
    
    // Update loading state
    this.loadingStates.set(trackId, {
      isLoading: false,
      error: isFallback ? "Using silent fallback due to audio load failure" : null,
      usingSilentFallback: true
    });
    
    const mode = isFallback ? "fallback silent" : "silent";

    return Promise.resolve(audio);
  }
  
  /**
   * Play a track
   */
  playTrack(trackId: string, loop: boolean = true, volume: number = 0.5): Promise<void> {
    return this.getAudio(trackId)
      .then(audio => {
        // Reset the audio
        try {
          audio.currentTime = 0;
        } catch (error) {
          console.warn('Could not reset audio position', error);
        }
        
        // Configure audio
        audio.loop = loop;
        
        // Set volume (unless it's silent fallback)
        const state = this.getLoadingState(trackId);
        if (!state.usingSilentFallback) {
          audio.volume = volume;
        }
        
        // Start playing
        const playPromise = audio.play();
        
        // Handle browsers that don't return a promise from play()
        if (playPromise) {
           return playPromise.then(() => {});
        } else {
           return Promise.resolve();
        }
      });
  }
  
  /**
   * Stop a track
   */
  stopTrack(trackId: string): void {
    const audio = this.audioCache.get(trackId);
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;

      } catch (err) {
        console.error(`Error stopping track ${trackId}:`, err);
      }
    }
  }
  
  /**
   * Get the loading state for a track
   */
  getLoadingState(trackId: string): AudioLoadState {
    return this.loadingStates.get(trackId) || {
      isLoading: false,
      error: null,
      usingSilentFallback: false
    };
  }
  
  /**
   * Clear audio cache
   */
  clearCache(): void {
    this.audioCache.forEach(audio => {
      try {
        audio.pause();
      } catch (error) {
        console.error('Error clearing audio cache:', error);
      }
    });
    
    this.audioCache.clear();
    this.loadingStates.clear();

  }
  
  /**
   * Run diagnostics
   */
  async runDiagnostics(): Promise<{[key: string]: string}> {
    const results: {[key: string]: string} = {};
    
    // Check if we're in silent-only mode
    if (this.useSilentAudioOnly) {
      results['mode'] = '⚠️ Using silent-only mode (real audio disabled)';
    }
    
    // Test silent audio
    try {
      const silentAudio = this.createSilentAudio();
      results['silent_audio'] = '✅ Silent audio simulation is available';
    } catch (error) {
      results['silent_audio'] = '❌ Silent audio failed: ' + String(error);
    }
    
    // Only test real audio if not in silent-only mode
    if (!this.useSilentAudioOnly) {
      // Test each real audio track
      for (const track of musicTracks) {
        const trackKey = `${track.id}_real`;
        const filename = trackFileMap[track.id];
        const audioUrl = `/audio/${filename}`;
        
        try {
          // Create an audio element but don't actually load it fully
          const audio = new Audio(audioUrl);
          
          // Just check if the URL is valid
          results[trackKey] = `✅ Audio file exists for "${track.name}" at ${audioUrl}`;
        } catch (error) {
          results[trackKey] = `❌ Error with "${track.name}": ${String(error)}`;
        }
      }
    }
    
    // Test silent fallbacks for all tracks
    for (const track of musicTracks) {
      results[`${track.id}_simulation`] = `✅ Audio simulation available for "${track.name}"`;
    }
    
    // Record our current mode
    results['note'] = this.useSilentAudioOnly 
      ? '⚠️ Using silent audio simulation only (real audio disabled)'
      : '✅ Using real audio with silent fallback';
    
    return results;
  }
  
  /**
   * Check if audio is supported
   */
  checkAudioSupport(): {supported: boolean, details: string} {
    // Create a test audio element
    try {
      const audio = new Audio();
      const canPlay = audio && typeof audio.canPlayType === 'function';
      
      if (!canPlay) {
        return { 
          supported: false, 
          details: 'Audio not supported in this browser' 
        };
      }
      
      // Check MP3 support
      const canPlayMp3 = audio.canPlayType('audio/mpeg').replace(/no/, '');
      
      if (!canPlayMp3) {
        return {
          supported: true,
          details: 'Browser supports audio but not MP3 format - using silent fallback'
        };
      }
      
      return {
        supported: true,
        details: 'Audio fully supported in this browser'
      };
    } catch (e) {
      return {
        supported: false,
        details: `Audio error: ${String(e)}`
      };
    }
  }
}

// Export singleton
export const audioService = new AudioService();
export default audioService; 